import { useMutation, useQuery } from "@apollo/client";
import { useAuth } from "providers/AuthProvider";
import { useI18n } from "providers/I18nProvider";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import {
  CustomAttributesQueryQuery,
  FormCategory,
  gql,
  UpsertCustomAttributeInput,
} from "types";
import z from "zod";

type CustomAttributeEntity =
  | "CompanyEntity"
  | "EmployeeEntity"
  | "NomineeEntity"
  | "ProductInstanceEntity";
type CustomAttribute =
  CustomAttributesQueryQuery["customAttributes"]["list"][number];

type ContextType = {
  attributes: CustomAttribute[];
  findEntityAttributes: (
    entityName: CustomAttributeEntity,
  ) => CustomAttribute[];
  findEntityAndLocationAttributes: (
    entityName: CustomAttributeEntity,
    category: FormCategory,
  ) => CustomAttribute[];
  findAttributesByLocation: (category: FormCategory) => CustomAttribute[];
  hasCustomAttribute: (
    entityName: CustomAttributeEntity,
    attributeName: string,
  ) => boolean;
  upsertCustomAttributes: (
    customAttributes: UpsertCustomAttributeInput[],
    category: FormCategory,
  ) => Promise<CustomAttribute[]>;
  useSchemaWithCustomAttributes: <T extends z.ZodSchema>(
    schema: T,
    customAttributes: CustomAttribute[],
  ) => T;
  customAttributesIsLoaded: boolean;
};

type Props = {
  children: ReactNode;
};

export const CustomAttributesContext = createContext<ContextType>({
  attributes: [],
  findEntityAttributes: () => [],
  findEntityAndLocationAttributes: () => [],
  findAttributesByLocation: () => [],
  hasCustomAttribute: () => false,
  upsertCustomAttributes: () => Promise.resolve([]),
  customAttributesIsLoaded: false,
  useSchemaWithCustomAttributes: <T extends z.ZodSchema>(schema: T) => schema,
});

const CUSTOM_ATTRIBUTES_QUERY = gql(/* GraphQL */ `
  query CustomAttributesQuery {
    customAttributes {
      list {
        id
        validator
        updatedAt
        type
        title
        required
        placeholder
        options
        data
        name
        hint
        hidden
        displayedOnAdmin
        entity
        editable
        category
        order
        fundDocuments {
          id
          createdAt
          filename
          mimeType
          size
          updatedAt
        }
      }
    }
  }
`);

const UPSERT_CUSTOM_ATTRIBUTES = gql(/* GraphQL */ `
  mutation UpsertCustomAttributes(
    $input: [UpsertCustomAttributeInput!]!
    $category: String!
  ) {
    upsertCustomAttributes(customAttributes: $input, category: $category) {
      id
      validator
      updatedAt
      type
      title
      required
      placeholder
      options
      data
      name
      hint
      hidden
      displayedOnAdmin
      entity
      editable
      category
      order
      fundDocuments {
        id
        createdAt
        filename
        mimeType
        size
        updatedAt
      }
    }
  }
`);

export const CustomAttributesProvider = ({ children }: Props) => {
  const { authState } = useAuth();
  const { data, loading } = useQuery(CUSTOM_ATTRIBUTES_QUERY, {
    skip: !authState?.accessToken,
  });
  const [upsertCustomAttributesMutation] = useMutation(
    UPSERT_CUSTOM_ATTRIBUTES,
  );
  const { t, language } = useI18n();

  const attributes = useMemo(
    () => data?.customAttributes?.list || [],
    [data?.customAttributes?.list],
  );

  const attributesByEntity = useMemo(() => {
    const map = new Map<CustomAttributeEntity, CustomAttribute[]>();
    attributes.forEach((attr) => {
      const existing = map.get(attr.entity as CustomAttributeEntity) || [];
      map.set(attr.entity as CustomAttributeEntity, [...existing, attr]);
    });
    return map;
  }, [attributes]);

  const attributesByCategory = useMemo(() => {
    const map = new Map<FormCategory, CustomAttribute[]>();
    attributes.forEach((attr) => {
      const existing = map.get(attr.category) || [];
      map.set(attr.category, [...existing, attr]);
    });
    return map;
  }, [attributes]);

  const attributeEntityNameSet = useMemo(() => {
    const set = new Set<string>();
    attributes.forEach((attr) => {
      set.add(`${attr.entity}:${attr.name}`);
    });
    return set;
  }, [attributes]);

  const findEntityAttributes = useCallback(
    (entityName: CustomAttributeEntity): CustomAttribute[] => {
      return attributesByEntity.get(entityName) || [];
    },
    [attributesByEntity],
  );

  const findAttributesByLocation = useCallback(
    (category: FormCategory): CustomAttribute[] => {
      return attributesByCategory.get(category) || [];
    },
    [attributesByCategory],
  );

  const findEntityAndLocationAttributes = useCallback(
    (
      entityName: CustomAttributeEntity,
      category: FormCategory,
    ): CustomAttribute[] => {
      const entityAttrs = attributesByEntity.get(entityName) || [];
      return entityAttrs.filter((attr) => attr.category === category);
    },
    [attributesByEntity],
  );

  const hasCustomAttribute = useCallback(
    (entityName: CustomAttributeEntity, attributeName: string): boolean => {
      return attributeEntityNameSet.has(`${entityName}:${attributeName}`);
    },
    [attributeEntityNameSet],
  );

  const upsertCustomAttributes = useCallback(
    async (
      customAttributes: UpsertCustomAttributeInput[],
      category: FormCategory,
    ): Promise<CustomAttribute[]> => {
      const { data } = await upsertCustomAttributesMutation({
        variables: { input: customAttributes, category },
        refetchQueries: [{ query: CUSTOM_ATTRIBUTES_QUERY }],
      });
      return data?.upsertCustomAttributes || [];
    },
    [upsertCustomAttributesMutation],
  );

  const customAttributesIsLoaded = Boolean(
    !loading && authState?.accessToken && attributes.length,
  );

  const useSchemaWithCustomAttributes = useCallback(
    <T extends z.ZodSchema>(
      schema: T,
      customAttributes: CustomAttribute[],
    ): T => {
      if (!customAttributes.length) return schema;

      return schema.superRefine((data, ctx) => {
        for (const attr of customAttributes) {
          let value = data?.customAttributes?.[attr.name];

          if (typeof value === "string") {
            value = value.trim();
          }

          const errorMessage = t(
            "apps.admin.common.customAttributeRequired",
            attr.title?.[language].toString(),
          );

          if (attr.required && !value) {
            ctx.addIssue({
              path: ["customAttributes", attr.name],
              code: z.ZodIssueCode.custom,
              message: errorMessage,
            });
          }
        }
      }) as unknown as T;
    },
    [t, language],
  );

  const contextValue = useMemo(
    () => ({
      attributes,
      findEntityAttributes,
      findEntityAndLocationAttributes,
      findAttributesByLocation,
      hasCustomAttribute,
      upsertCustomAttributes,
      customAttributesIsLoaded,
      useSchemaWithCustomAttributes,
    }),
    [
      attributes,
      findEntityAttributes,
      findEntityAndLocationAttributes,
      findAttributesByLocation,
      hasCustomAttribute,
      upsertCustomAttributes,
      customAttributesIsLoaded,
      useSchemaWithCustomAttributes,
    ],
  );

  return (
    <CustomAttributesContext.Provider value={contextValue}>
      {children}
    </CustomAttributesContext.Provider>
  );
};

export const useCustomAttributes = () => {
  const context = useContext(CustomAttributesContext);
  if (!context) {
    throw new Error(
      "useCustomAttributes must be used within a CustomAttributesProvider",
    );
  }
  return context;
};
